import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CalendarClock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useActiveHeatmap } from '@/hooks/useDashboardAnalytics';

interface ActiveHeatmapWidgetProps {
  days?: number;
  className?: string;
}

// Postgres DOW: 0=Sunday, 1=Monday, ..., 6=Saturday.
// Renderим в пользовательском порядке ПН..ВС.
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;
const DAY_LABELS: Record<number, string> = {
  0: 'Вс',
  1: 'Пн',
  2: 'Вт',
  3: 'Ср',
  4: 'Чт',
  5: 'Пт',
  6: 'Сб',
};
const FULL_DAY_LABELS: Record<number, string> = {
  0: 'Воскресенье',
  1: 'Понедельник',
  2: 'Вторник',
  3: 'Среда',
  4: 'Четверг',
  5: 'Пятница',
  6: 'Суббота',
};
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function cellKey(dayOfWeek: number, hour: number): string {
  return `${dayOfWeek}:${hour}`;
}

/** Цвет ячейки по нормализованной интенсивности 0..1. */
function cellColor(intensity: number): string {
  if (intensity === 0) {
    return 'var(--muted)';
  }
  // muted → primary. Linear interpolate transparency of primary.
  const alpha = Math.max(0.08, Math.min(1, intensity));
  return `hsla(221, 83%, 53%, ${alpha.toFixed(2)})`;
}

function textColorForIntensity(intensity: number): string {
  return intensity > 0.55 ? 'text-slate-50' : 'text-foreground/80';
}

export function ActiveHeatmapWidget({ days = 7, className }: ActiveHeatmapWidgetProps) {
  const { data, isLoading } = useActiveHeatmap(days);

  const countsMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const cell of data?.cells ?? []) {
      map.set(cellKey(cell.dayOfWeek, cell.hour), cell.count);
    }
    return map;
  }, [data?.cells]);

  const maxCount = useMemo(() => {
    let max = 0;
    for (const count of countsMap.values()) {
      if (count > max) {max = count;}
    }
    return max;
  }, [countsMap]);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            Активность по часам
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[220px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (maxCount === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            Активность по часам
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            Нет событий за последние {days} дней
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <CalendarClock className="h-4 w-4" />
          Активность по часам
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          События за последние {days} дней
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="px-4 pb-4">
          <TooltipProvider delayDuration={100}>
            <div className="min-w-[640px] space-y-1">
              {/* Hour header */}
              <div
                className="grid gap-[2px] text-[10px] text-muted-foreground"
                style={{ gridTemplateColumns: '32px repeat(24, 1fr)' }}
              >
                <div />
                {HOURS.map((h) => (
                  <div key={`header-h${h}`} className="text-center">
                    {h % 3 === 0 ? h : ''}
                  </div>
                ))}
              </div>

              {DAY_ORDER.map((dow) => (
                <div
                  key={`row-dow${dow}`}
                  className="grid gap-[2px] items-stretch"
                  style={{ gridTemplateColumns: '32px repeat(24, 1fr)' }}
                >
                  <div className="flex items-center text-xs text-muted-foreground">
                    {DAY_LABELS[dow]}
                  </div>
                  {HOURS.map((hour) => {
                    const count = countsMap.get(cellKey(dow, hour)) ?? 0;
                    const intensity = count === 0 ? 0 : count / maxCount;
                    return (
                      <Tooltip key={`cell-${dow}-${hour}`}>
                        <TooltipTrigger asChild>
                          <div
                            className={`h-7 rounded-sm flex items-center justify-center text-[10px] font-medium tabular-nums ${textColorForIntensity(intensity)}`}
                            style={{ backgroundColor: cellColor(intensity) }}
                          >
                            {count > 0 && intensity > 0.25 ? count : ''}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="center">
                          <p className="font-medium">
                            {FULL_DAY_LABELS[dow]}, {String(hour).padStart(2, '0')}:00
                          </p>
                          <p className="text-muted-foreground">Событий: {count}</p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              ))}

              {/* Legend */}
              <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
                <span>Меньше</span>
                <div className="flex gap-[2px]">
                  {[0, 0.2, 0.4, 0.6, 0.8, 1].map((intensity) => (
                    <div
                      key={`legend-${intensity}`}
                      className="h-3 w-4 rounded-sm"
                      style={{ backgroundColor: cellColor(intensity) }}
                    />
                  ))}
                </div>
                <span>Больше</span>
                <span className="ml-auto tabular-nums">макс: {maxCount}</span>
              </div>
            </div>
          </TooltipProvider>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
